import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES, BRAND } from "../data/content";
import { FinalCTA } from "./Home";

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 bg-brand-ink text-brand-cream overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: `url(${IMAGES.hinterland})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/60 to-brand-ink/95" />
        <div className="container-px relative z-10">
          <div className="overline text-brand-ochre">About Talking Trails</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.04] max-w-4xl">
            Our vision for <span className="italic font-light">place-based storytelling.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-brand-cream/85 text-lg font-light leading-relaxed">
            Places hold stories. Stories of people, culture, history, landscape and
            belonging. Yet too often those stories are disconnected from the places
            where they happened. Talking Trails exists to help communities preserve,
            share and celebrate their stories through immersive audio experiences,
            interpretive signage and digital storytelling that connects people to place.
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Our intent</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">
              Meaning emerges through listening.
            </h2>
            <img
              src={BRAND.logo}
              alt="Talking Trails"
              className="mt-10 h-40 md:h-48 w-auto object-contain"
            />
            <div className="hairline mt-10" />
          </div>
          <div className="lg:col-span-7 text-brand-muted text-lg font-light leading-relaxed space-y-5">
            <p>
              As travellers increasingly seek meaningful experiences and genuine
              connections, storytelling offers a powerful way to connect people with place.
            </p>
            <p>
              Talking Trails was created to help communities preserve knowledge, share
              local stories and create experiences that deepen understanding of culture,
              history and landscape.
            </p>
            <p>
              We believe the most memorable experiences are not built through technology
              alone.
            </p>
            <p>
              They emerge through listening. Through context. Through trust. Through the
              voices of Elders, community members, historians, rangers and local people
              who know a place best.
            </p>
            <p>
              When stories are shared where they belong, visitors don&rsquo;t simply learn
              about a place.
            </p>
            <p className="font-serif italic text-2xl md:text-3xl text-brand-charcoal not-italic">
              <span className="italic font-light">They experience it.</span>
            </p>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="bg-brand-sand py-24 md:py-32">
        <div className="container-px max-w-5xl">
          <div className="overline">Three Things We Believe</div>
          <div className="mt-12 space-y-14">
            {[
              {
                title: "Listening creates understanding.",
                body: "Stories help us see the world through someone else's eyes.",
              },
              {
                title: "Listening strengthens community.",
                body: "Shared stories build connection, belonging and pride in place.",
              },
              {
                title: "Listening deepens our connection to place.",
                body: "When we understand the stories of a landscape, we better understand where we are and our relationship to it.",
              },
            ].map((item, i) => (
              <div key={item.title} className="grid grid-cols-12 gap-6 md:gap-10 items-start">
                <span className="col-span-2 md:col-span-1 font-serif text-3xl md:text-4xl text-brand-ochre font-light pt-2">
                  0{i + 1}
                </span>
                <div className="col-span-10 md:col-span-11">
                  <h3 className="font-serif text-3xl md:text-5xl text-brand-charcoal leading-[1.15]">
                    {item.title}
                  </h3>
                  <p className="mt-4 text-brand-muted text-lg md:text-xl font-light leading-relaxed max-w-3xl">
                    {item.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE STRIP — featuring authentic photography */}
      <section className="py-20 md:py-28">
        <div className="container-px grid grid-cols-1 md:grid-cols-12 gap-4">
          <div
            className="md:col-span-5 aspect-[4/5] bg-cover bg-center"
            style={{ backgroundImage: `url(${BRAND.elderStoryteller})` }}
          />
          <div className="md:col-span-7 grid grid-rows-2 gap-4">
            <div
              className="aspect-[16/7] bg-cover bg-center"
              style={{ backgroundImage: `url(${BRAND.familyOnTrail})` }}
            />
            <div
              className="aspect-[16/7] bg-cover bg-center"
              style={{ backgroundImage: `url(${BRAND.studioStoryteller})` }}
            />
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-brand-ink text-brand-cream py-24 md:py-32">
        <div className="container-px">
          <div className="overline text-brand-ochre">Working principles</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight max-w-3xl">
            We work slowly, on Country, and with consent.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-cream/15">
            {[
              { t: "Community-led", d: "Stories belong to the people who carry them. We follow community direction and cultural authority." },
              { t: "Place-based", d: "Every trail starts with the landscape. We listen first to the place." },
              { t: "Story-driven", d: "Technology is the vessel, never the point. The story sets the pace." },
              { t: "Future-facing", d: "We design experiences that evolve — with new voices, new seasons, new ways of listening." },
            ].map((v, i) => (
              <div key={v.t} className="bg-brand-ink p-8 md:p-10">
                <div className="overline text-brand-ochre">0{i + 1}</div>
                <h3 className="font-serif text-2xl md:text-3xl mt-4 text-brand-cream">{v.t}</h3>
                <p className="mt-3 text-brand-cream/65 text-sm font-light leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-brand-ochre text-brand-cream px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-brand-ochreDark transition-colors"
            >
              Work with us <ArrowRight size={14} />
            </Link>
            <Link
              to="/become-a-storyteller"
              className="inline-flex items-center justify-center gap-2 border border-brand-cream/80 text-brand-cream px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-brand-cream hover:text-brand-charcoal transition-colors"
            >
              Become a Storyteller
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
